import * as spreadsheetApi from '@/services/spreadsheet.service'
import * as menuHiveApi from '@/services/api.service'
import { withRetry } from '@/utils/retry.utils'
import { productKeyMap, comboKeyMap, specificationKeyMap, comboSubItemsKeyMap, comboSubAnyKeyMap, comboSubAltKeyMap, retailGroupKeyMap } from '@/static/docs/menuKeyMap'
import spreadSheetModel from '@/models/spreadSheet.model'
import commonUtils from "@/utils/common.utils";
import { getSpecOptionV3Price, getSpecOptionV3Available } from "@/utils/productName.utils";

// 逗號分隔的 id 字串轉陣列：去頭尾空白、濾掉空值。
// DB 與試算表的 id 欄位常夾帶空白（例："product_017, product_018"），
// 直接用 === 比對會對不到商品，故在資料入口統一正規化。
const parseIdList = (value) => {
    if (Array.isArray(value)) return value.map(id => String(id ?? '').trim()).filter(id => id)
    return value ? String(value).split(',').map(id => id.trim()).filter(id => id) : []
}

export default {
    namespaced: true,
    state: {
        dineInTablesResponse: [],
        dineInTablesLoaded: false,
        categoriesResponse: [],
        productsResponse: [],
        combosResponse: [],
        specificationResponse: [],
        comboSubItemsResponse: [],
        comboSubAnyResponse: [],
        comboSubAltResponse: [],
        productComboSpecResponse: [],
        spreadSheetData: {},
        dbMenuData: null,
        useDbData: false,
        retailGroupsResponse: [],
        dbServeAreas: null,
        dbServeTables: null,
        dbStockItems: [],
        // 第二層背景載入的 promise（spreadsheet 不在 persistedstate paths，可安全存放）
        secondaryLoadPromise: null
    },
    getters: {
        isVip: (state, getters, rootState, rootGetters) => {
            return rootGetters['userData/vipLevel'] > 1
        },
        vipPriceChannelIndex: () => {
            // VIP價格格式：POS,LINE點餐,無人點餐機；目前前台點餐使用 LINE點餐。
            return 1
        },
        isAreaIdValid: (state, getters) => (areaId) => {
            if (!getters.dineInTables.length) return false
            return getters.dineInTables.find(area => area.areaId === areaId)
        },
        isTableIndexValid: (state, getters) => (areaId, tableIndex) => {
            if (!getters.dineInTables.length) return false
            const area = getters.dineInTables.find(area => area.areaId === areaId)
            if (!area) return false
            // 如果沒有 tableIndex，且該區域沒有桌子，則回傳 true
            if (tableIndex == -1 && area.tables.length === 0) {
                return true
            }
            return area.tables[tableIndex]
        },
        tableName: (state, getters) => (areaId, tableIndex) => {
            if (!getters.dineInTables.length) return ''
            if (!getters.isAreaIdValid(areaId) || !getters.isTableIndexValid(areaId, tableIndex)) return ''
            const area = getters.dineInTables.find(area => area.areaId === areaId)
            if (tableIndex == -1) return area.name
            const tableName = area.tables[tableIndex] || ''
            return `${area.name}${tableName.name}`
        },
        dineInTables: (state) => {
            // 優先使用獨立載入的 dbServeAreas（OnlineOrder 路徑）
            // 其次使用 loadMenuData 一起載入的 dbMenuData.serveAreas（Ordering 路徑）
            const serveAreas = state.dbServeAreas || (state.useDbData && state.dbMenuData ? state.dbMenuData.serveAreas : null)
            if (serveAreas) {
                const serveTables = state.dbServeTables || []
                return serveAreas
                    .filter(area => area.enable)
                    .slice()
                    .sort((a, b) => a.sortingOrder - b.sortingOrder)
                    .map(area => ({
                        areaId: area.id,
                        name: area.name,
                        enable: area.enable,
                        tables: serveTables
                            .filter(table => table.serve_area_id === area.id)
                            .map(table => ({
                                id: table.id,
                                name: table.name,
                                serveAreaId: table.serve_area_id
                            }))
                    }))
            }
            const result = [];
            let currentArea = null;
            for (const row of state.dineInTablesResponse) {
                if (row.length === 0) {
                    continue;
                }
                const [areaId, name, enable] = row;
                if (areaId) {
                    currentArea = { areaId, name, enable: enable === "true", tables: [] };
                    result.push(currentArea);
                } else {
                    currentArea.tables.push({ name });
                }
            }
            console.log('用餐區域桌號', result)
            return result.filter(area => area.enable);
        },
        categories: (state, getters) => {
            if (state.useDbData && state.dbMenuData) {
                const parseV3Available = (v) => {
                    const parts = v ? String(v).split(',') : []
                    return parts.length >= 2 ? parts[1] === '1' : false
                }
                return state.dbMenuData.categories
                    .filter(cat => cat.id && parseV3Available(cat.availabilityV3))
                    .map(cat => ({
                        categoryId: cat.id,
                        categoryName: cat.name,
                        enable: parseV3Available(cat.availabilityV3),
                        categoryItems: cat.goodIDs ? cat.goodIDs.split(',').filter(id => id) : [],
                        sortingOrder: Number(cat.sortingOrder) || 0
                    }))
                    .sort((a, b) => a.sortingOrder - b.sortingOrder)
            }
            const result = [];
            for (let i = 2; i < state.categoriesResponse.length; i++) {
                const [categoryId, categoryName, enable, ...categoryItems] = state.categoriesResponse[i];
                result.push({
                    categoryId,
                    categoryName,
                    enable: getters.parseAvailableInStore(enable),
                    categoryItems: categoryItems.filter(item => item)
                });
            }
            return result.filter(category => {
                return category.enable && category.categoryId
            });
        },
        retailGroups: (state) => {
            if (!Array.isArray(state.retailGroupsResponse)) return []

            const parseLineAvailable = (v) => {
                const parts = v ? String(v).split(',').map(part => part.trim()) : []
                if (parts.length >= 2) return parts[1] === '1' || parts[1].toLowerCase() === 'true'
                if (v === true || v === 1 || v === '1') return true
                if (v === false || v === 0 || v === '0') return false
                if ((/true/).test(String(v).toLowerCase())) return true
                return false
            }

            return state.retailGroupsResponse
                .filter(group => group && group.id && parseLineAvailable(group.availabilityV3 ?? group.availableInStore))
                .map(group => ({
                    id: group.id,
                    name: group.name,
                    displayName: group.displayName,
                    printName: group.printName || '',
                    imageUrl: group.imageUrl || null,
                    description: group.description || '',
                    backgroundColor: group.backgroundColor || '',
                    sortingOrder: Number(group.sortingOrder) || 0,
                    productIds: Array.isArray(group.productIds)
                        ? group.productIds.filter(id => id)
                        : (group.productIDs ? group.productIDs.split(',').filter(id => id) : [])
                }))
                .filter(group => group.productIds.length > 0)
                .sort((a, b) => a.sortingOrder - b.sortingOrder)
        },
        productsInRetailGroup: (state, getters) => (groupId) => {
            const group = getters.retailGroups.find(item => item.id === groupId)
            if (!group) return []

            return group.productIds
                .map(id => getters.productAndComboList.find(product => product.id === id))
                .filter(product => product && product.availableInStore)
        },
        products: (state, getters) => {
            if (state.useDbData && state.dbMenuData) {
                const parseV3Available = (v) => {
                    const parts = v ? String(v).split(',') : []
                    return parts.length >= 2 ? parts[1] === '1' : false
                }
                const parseV3Price = (v) => {
                    const parts = v ? String(v).split(',') : []
                    if (parts.length >= 2) return Number(parts[1])
                    if (parts.length === 1) return Number(parts[0])
                    return 0.0
                }
                const stockItemsByProductId = (state.dbStockItems || []).reduce((acc, item) => {
                    if (item && item.item_id) acc[item.item_id] = item
                    return acc
                }, {})
                return state.dbMenuData.products.map(p => {
                    const productSpecSettings = typeof p.productSpecSettings === 'string'
                        ? JSON.parse(p.productSpecSettings)
                        : (p.productSpecSettings || [])
                    const standardPrice = parseV3Price(p.standardPriceV3)
                    const vipPrice = getters.parseVipPrice(p.vipPriceV3, standardPrice)
                    const stockMode = Number(p.stockMode) || 0
                    const stockItem = stockItemsByProductId[p.id]
                    const stockCount = stockMode === 1 ? Number(stockItem?.stock_count || 0) : null
                    return {
                        id: p.id,
                        name: p.name,
                        standardPrice,
                        vipPrice,
                        price: getters.isVip ? vipPrice : standardPrice,
                        availableInStore: parseV3Available(p.availabilityV3),
                        cost: p.cost ? Number(p.cost) : 0,
                        isTaxFree: p.isTaxFree || false,
                        isDynamicPrice: p.isDynamicPrice || false,
                        stockMode,
                        stockCount,
                        isStockSoldOut: stockMode === 1 && stockCount === 0,
                        autoReplenishCount: p.autoReplenishCount || 0,
                        imageUrl: p.imageUrl || null,
                        backgroundColor: p.backgroundColor || '',
                        description: p.description || '',
                        skuCode: p.skuCode || '',
                        internationalCode: p.internationalCode || '',
                        productSpecSettings,
                        sortingOrder: p.sortingOrder || 0
                    }
                })
            }
            let data = [...state.productsResponse];
            const headers = data[1];
            const result = [];
            // 將 data 每個 row 長度都補齊與 headers 一樣長, 往後補將值設為 null
            data = data.map(row => {
                const diff = headers.length - row.length;
                if (diff > 0) {
                    for (let i = 0; i < diff; i++) {
                        row.push(null);
                    }
                }
                return row;
            });

            for (let i = 2; i < data.length; i++) {
                const item = {};
                const productSpecSettings = [];

                for (let index = 0; index < data[i].length; index++) {
                    const value = data[i][index];
                    const key = productKeyMap[headers[index]];

                    if (index >= 15) {
                        if (!value) {
                            index += 6;
                            continue;
                        }
                        productSpecSettings.push({
                            "subSpecID": value,
                            "mustHave": data[i][index + 1] ? getters.toBoolean(data[i][index + 1]) : false,
                            "frontEndDisplayName": data[i][index + 2],
                            "isSingleSelection": data[i][index + 3] ? getters.toBoolean(data[i][index + 3]) : false,
                            // 預設為 0
                            "multiSelectionMin": data[i][index + 4] ? Number(data[i][index + 4]) : 0,
                            // 預設為 1
                            "multiSelectionMax": data[i][index + 5] ? Number(data[i][index + 5]) : 1,
                            "allowSelectManyTimes": data[i][index + 6] ? getters.toBoolean(data[i][index + 6]) : false
                        });
                        index += 6;
                    } else {
                        if (key === 'autoReplenishCount') item[key] = value ? Number(value) : null;
                        else if (key === 'standardPrice') item[key] = getters.parsePrice(value);
                        else if (key === 'availableInStore') item[key] = getters.parseAvailableInStore(value);
                        else if (key === 'cost') item[key] = value ? Number(value) : 0.0;
                        else if (key === 'isTaxFree') item[key] = value ? getters.toBoolean(value) : false;
                        // stockLimit 不需要
                        else if (key === 'stockLimit') continue
                        else if (key === 'vipPrice') item[key] = getters.parseVipPrice(value, item['standardPrice']);
                        else if (key === 'isDynamicPrice') item[key] = value ? getters.toBoolean(value) : false;
                        else if (key === 'stockMode') item[key] = value ? Number(value) : 0;
                        else {
                            // 使用表單資料
                            item[key] = value;
                        }
                    }
                }
                item["productSpecSettings"] = productSpecSettings;
                item['price'] = getters.isVip ? item['vipPrice'] : item['standardPrice'];
                result.push(item);
            }
            return result;
        },
        combos: (state, getters) => {
            if (state.useDbData && state.dbMenuData) {
                const parseV3Available = (v) => {
                    const parts = v ? String(v).split(',') : []
                    return parts.length >= 2 ? parts[1] === '1' : false
                }
                const parseV3Price = (v) => {
                    const parts = v ? String(v).split(',') : []
                    if (parts.length >= 2) return Number(parts[1])
                    if (parts.length === 1) return Number(parts[0])
                    return 0.0
                }
                return state.dbMenuData.combos.map(c => {
                    const comboSubSettings = typeof c.comboSubSettings === 'string'
                        ? JSON.parse(c.comboSubSettings)
                        : (c.comboSubSettings || [])

                    // 解析 comboSubAnySetting，如果是字串則進行 JSON 解析
                    const comboSubAnySetting = (() => {
                        if (!c.comboSubAnySetting) return null
                        if (typeof c.comboSubAnySetting === 'string') {
                            try {
                                return JSON.parse(c.comboSubAnySetting)
                            } catch (e) {
                                console.error('Failed to parse comboSubAnySetting:', e)
                                return null
                            }
                        }
                        return c.comboSubAnySetting
                    })()

                    const standardPrice = parseV3Price(c.standardPriceV3)
                    const vipPrice = getters.parseVipPrice(c.vipPriceV3, standardPrice)
                    return {
                        id: c.id,
                        name: c.name,
                        standardPrice,
                        vipPrice,
                        price: getters.isVip ? vipPrice : standardPrice,
                        availableInStore: parseV3Available(c.availabilityV3),
                        cost: c.cost || 0,
                        isTaxFree: c.isTaxFree || false,
                        isDynamicPrice: c.isDynamicPrice || false,
                        comboMode: c.comboMode || 0,
                        imageUrl: c.imageUrl || null,
                        backgroundColor: c.backgroundColor || '',
                        description: c.description || '',
                        skuCode: c.skuCode || '',
                        internationalCode: c.internationalCode || '',
                        productIDs: parseIdList(c.productIDs),
                        comboSubSettings,
                        comboSubAnySetting,
                        sortingOrder: c.sortingOrder || 0
                    }
                })
            }
            let data = [...state.combosResponse];
            const headers = data[1];
            const result = [];
            // 將 data 每個 row 長度都補齊與 headers 一樣長, 往後補將值設為 null
            data = data.map(row => {
                const diff = headers.length - row.length;
                if (diff > 0) {
                    for (let i = 0; i < diff; i++) {
                        row.push(null);
                    }
                }
                return row;
            });

            for (let i = 2; i < data.length; i++) {
                const item = {};
                const productIDs = [];
                const comboSubSettings = [];
                let comboSubAnySetting = {};

                for (let index = 0; index < data[i].length; index++) {
                    const value = data[i][index];
                    const key = comboKeyMap[headers[index]];

                    if (index >= 16 && index <= 25) {
                        if (!value) {
                            continue;
                        }
                        productIDs.push(value);
                    } else if (index >= 26 && index <= 60) {
                        if (!value) {
                            index += 6;
                            continue;
                        }
                        comboSubSettings.push({
                            "subSpecID": value,
                            "mustHave": data[i][index + 1] ? getters.toBoolean(data[i][index + 1]) : false,
                            "frontEndDisplayName": data[i][index + 2],
                            "isSingleSelection": data[i][index + 3] ? getters.toBoolean(data[i][index + 3]) : false,
                            // 預設為 0
                            "multiSelectionMin": data[i][index + 4] ? Number(data[i][index + 4]) : 0,
                            // 預設為 1
                            "multiSelectionMax": data[i][index + 5] ? Number(data[i][index + 5]) : 1,
                            "allowSelectManyTimes": data[i][index + 6] ? getters.toBoolean(data[i][index + 6]) : false
                        });
                        index += 6;
                    } else if (index >= 61 && index <= 64) {
                        if (!value) {
                            continue;
                        }
                        comboSubAnySetting = {
                            "comboSubAnyID": value,
                            "frontEndDisplayName": data[i][index + 1],
                            "selectionCount": data[i][index + 2] ? Number(data[i][index + 2]) : 1,
                            "allowSelectManyTimes": data[i][index + 3] ? getters.toBoolean(data[i][index + 3]) : false
                        };
                        index += 3;
                    } else {
                        if (key === 'standardPrice') item[key] = getters.parsePrice(value);
                        else if (key === 'availableInStore') item[key] = getters.parseAvailableInStore(value);
                        else if (key === 'cost') item[key] = value ? Number(value) : 0.0;
                        else if (key === 'isTaxFree') item[key] = value ? getters.toBoolean(value) : false;
                        else if (key === 'stockLimit') continue
                        // dailyAutoReplenish 不需要
                        else if (key === 'dailyAutoReplenish') continue
                        // unlimitedStock 不需要
                        else if (key === 'unlimitedStock') continue
                        else if (key === 'vipPrice') item[key] = getters.parseVipPrice(value, item['standardPrice']);
                        else if (key === 'isDynamicPrice') item[key] = value ? getters.toBoolean(value) : false;
                        else if (key === 'comboMode') item[key] = value ? Number(value) : 0;
                        else if (key === 'enable') item[key] = value ? getters.toBoolean(value) : false;
                        else if (key === 'altBasePrice') item[key] = value ? Number(value) : '';
                        else {
                            // 使用表單資料
                            item[key] = value;
                        }
                    }
                }

                item["productIDs"] = parseIdList(productIDs);
                item["comboSubSettings"] = comboSubSettings;
                item["comboSubAnySetting"] = comboSubAnySetting.comboSubAnyID ? comboSubAnySetting : null;
                item['price'] = getters.isVip ? item['vipPrice'] : item['standardPrice'];
                result.push(item);
            }
            return result;
        },
        productComboSpec: (state, getters) => {
            if (state.useDbData && state.dbMenuData) {
                return state.dbMenuData.sheetSpecArrangements.flatMap(arr => {
                    const settings = typeof arr.specSettings === 'string'
                        ? JSON.parse(arr.specSettings)
                        : (arr.specSettings || [])
                    return settings.map(s => ({
                        id: s.subSpecID,
                        mustHave: s.mustHave || false,
                        frontEndDisplayName: s.frontEndDisplayName,
                        isSingleSelection: s.isSingleSelection || false,
                        multiSelectionMin: s.multiSelectionMin || 0,
                        multiSelectionMax: s.multiSelectionMax || 1,
                        allowSelectManyTimes: s.allowSelectManyTimes || false
                    })).filter(setting => setting.id)
                })
            }
            if (!state.productComboSpecResponse.length) return []
            const headers = state.productComboSpecResponse[1];
            if (!headers) return []
            const result = [];
            // 將 data 每個 row 長度都補齊與 headers 一樣長, 往後補將值設為 null
            const data = state.productComboSpecResponse.map(row => {
                const diff = headers.length - row.length;
                if (diff > 0) {
                    for (let i = 0; i < diff; i++) {
                        row.push(null);
                    }
                }
                return row;
            })
            for (let i = 2; i < data.length; i++) {
                for (let index = 0; index < data[i].length; index += 7) {
                    let item = {
                        id: data[i][index],
                        mustHave: data[i][index + 1] ? getters.toBoolean(data[i][index + 1]) : false,
                        frontEndDisplayName: data[i][index + 2],
                        isSingleSelection: data[i][index + 3] ? getters.toBoolean(data[i][index + 3]) : false,
                        multiSelectionMin: data[i][index + 4] ? Number(data[i][index + 4]) : 0,
                        multiSelectionMax: data[i][index + 5] ? Number(data[i][index + 5]) : 1,
                        allowSelectManyTimes: data[i][index + 6] ? getters.toBoolean(data[i][index + 6]) : false,
                    }
                    if (item.id) result.push(item);
                }
            }
            console.log('全單規格', result)
            return result;
        },
        specifications: (state, getters) => {
            if (state.useDbData && state.dbMenuData) {
                return state.dbMenuData.productSpecs.map(spec => {
                    const rawOptions = typeof spec.specOptions === 'string'
                        ? JSON.parse(spec.specOptions)
                        : (spec.specOptions || [])
                    // V3：availabilityV3 下架（明確 '0'）過濾；
                    // standardPriceV3 取 index1 覆寫 price，無值 fallback 原 price
                    const specOptions = rawOptions
                        .filter(o => getSpecOptionV3Available(o.availabilityV3))
                        .map(o => ({ ...o, price: getSpecOptionV3Price(o.standardPriceV3, o.price) }))
                    return {
                        id: spec.id,
                        name: spec.name,
                        enableForOrderSpec: spec.enableForOrderSpec || false,
                        enableForProductComboSpec: spec.enableForProductComboSpec || false,
                        stickerTagPrintName: spec.stickerTagPrintName || spec.name,
                        sortingOrder: spec.sortingOrder || 0,
                        specOptions
                    }
                })
            }
            let data = [...state.specificationResponse];
            const headers = data[1];
            const result = [];
            // 將 data 每個 row 長度都補齊與 headers 一樣長, 往後補將值設為 null
            data = data.map(row => {
                const diff = headers.length - row.length;
                if (diff > 0) {
                    for (let i = 0; i < diff; i++) {
                        row.push(null);
                    }
                }
                return row;
            });

            for (let i = 2; i < data.length; i++) {
                const item = {};
                const specOptions = [];

                for (let index = 0; index < data[i].length; index++) {
                    const value = data[i][index];
                    const key = specificationKeyMap[headers[index]];

                    if (index >= 5) {
                        if (!value) {
                            index += 4;
                            continue;
                        }
                        specOptions.push({
                            "id": commonUtils.generateId(),
                            "name": value,
                            "price": data[i][index + 1] ? Number(data[i][index + 1]) : 0.0,
                            "unLimitStock": data[i][index + 2] ? getters.toBoolean(data[i][index + 2]) : false,
                            // stockLimit 不需要
                            // "stockLimit": data[i][index + 3] ? Number(data[i][index + 3]) : 0,
                            "dailyAutoRestock": data[i][index + 4] ? getters.toBoolean(data[i][index + 4]) : false,
                        });
                        index += 4;
                    } else {
                        if (key === 'enableForOrderSpec') item[key] = value ? getters.toBoolean(value) : false;
                        else if (key === 'enableForProductComboSpec') item[key] = value ? getters.toBoolean(value) : false;
                        // 使用表單資料
                        else item[key] = value;
                    }
                }
                item["specOptions"] = specOptions;
                result.push(item);
            }
            console.log('規格', result)
            return result;
        },
        comboSubItems: (state, getters) => {
            if (state.useDbData && state.dbMenuData) {
                return state.dbMenuData.comboSubs.map(sub => ({
                    id: sub.id,
                    name: sub.name,
                    frontEndDisplayName: sub.frontEndDisplayName,
                    enable: sub.enable || false,
                    altBasePrice: sub.altBasePrice ? Number(sub.altBasePrice) : 0,
                    comboSubAltID: sub.comboSubAltID || '',
                    sortingOrder: sub.sortingOrder || 0,
                    productIDs: parseIdList(sub.productIDs)
                }))
            }
            let data = [...state.comboSubItemsResponse];
            const headers = data[1];
            const result = []

            for (let i = 2; i < data.length; i++) {
                const item = {};
                const productIDs = [];

                for (let index = 0; index < data[i].length; index++) {
                    const value = data[i][index];
                    const key = comboSubItemsKeyMap[headers[index]];

                    if (index >= 6) {
                        productIDs.push(value);
                    } else {
                        if (key === 'enable') item[key] = value ? getters.toBoolean(value) : false;
                        else if (key === 'altBasePrice') item[key] = value ? Number(value) : 0.0;
                        else if (key === 'comboSubAltID') item[key] = value ? value : '';
                        else {
                            item[key] = value ? value : null;
                        }
                    }
                }
                item["productIDs"] = parseIdList(productIDs);
                result.push(item)
            }
            console.log('套餐子項', result)
            return result;
        },
        productAndComboList: (state, getters) => {
            return [...getters.products, ...getters.combos];
        },
        menuItemList: (state, getters) => {
            return [
                ...getters.productAndComboList.map(item => ({ ...item, itemType: 'product' })),
                ...getters.retailGroups.map(item => ({ ...item, itemType: 'retailGroup' }))
            ];
        },
        productsInCategory: (state, getters) => {
            return getters.categories
                .filter(category => category.enable)
                .map(category => {
                    let categoryItems = category.categoryItems.map(item => {
                        return getters.menuItemList.find(menuItem => menuItem.id === item);
                    }).filter(item => item);
                    return {
                        category: category.categoryName,
                        products: categoryItems
                    };
                })
        },
        sheetVersion: (state) => {
            if (!state.categoriesResponse.length) return 2
            return Number(state.categoriesResponse[0][0]) || 2
        },
        parseAvailableInStore: (state, getters) => (value) => {
            if (getters.sheetVersion === 3) {
                const parts = value ? String(value).split(',') : []
                if (parts.length >= 2) return parts[1] === '1'
                return false
            }
            return value ? (/true/).test(value) : false
        },
        parsePrice: (state, getters) => (value) => {
            if (getters.sheetVersion === 3) {
                const parts = value ? String(value).split(',') : []
                if (parts.length >= 2) return Number(parts[1])
                if (parts.length === 1) return Number(parts[0])
                return 0.0
            }
            return value ? Number(value) : 0.0
        },
        parseVipPrice: (state, getters) => (value, standardPrice) => {
            if (!value) return standardPrice
            const parts = String(value).split(',').map(part => part.trim())
            const channelValue = parts.length === 1 ? parts[0] : parts[getters.vipPriceChannelIndex]
            const vipPrice = channelValue ? Number(channelValue) : 0
            return vipPrice > 0 ? vipPrice : standardPrice
        },
        toBoolean: () => (value) => {
            return (/true/).test(value)
        },
        comboSubAny: (state, getters) => {
            if (state.useDbData && state.dbMenuData) {
                return state.dbMenuData.comboSubAnyList.map(item => ({
                    comboSubAnyID: item.id, // 統一使用 comboSubAnyID 欄位名稱
                    id: item.id,
                    name: item.name,
                    enable: item.enable || false,
                    sortingOrder: item.sortingOrder || 0,
                    productIDs: parseIdList(item.productIDs)
                }))
            }
            let data = [...state.comboSubAnyResponse];
            const header = data[1];
            const result = [];
            for (let i = 2; i < data.length; i++) {
                const item = {}
                const productIDs = []

                for (let index = 0; index < data[i].length; index++) {
                    const value = data[i][index]
                    const key = comboSubAnyKeyMap[header[index]]

                    if (index >= 3) {
                        productIDs.push(value)
                    } else {
                        if (key === 'enable') item[key] = value ? getters.toBoolean(value) : false
                        else {
                            item[key] = value ? value : null
                        }
                    }
                }
                item["productIDs"] = parseIdList(productIDs)
                result.push(item)
            }
            console.log('任選子項', result)
            return result
        },
        comboSubAltItems: (state, getters) => {
            if (state.useDbData && state.dbMenuData) {
                return state.dbMenuData.comboSubAlts.map(alt => ({
                    id: alt.id,
                    name: alt.name,
                    enable: alt.enable || false,
                    sortingOrder: alt.sortingOrder || 0,
                    productIDs: parseIdList(alt.productIDs)
                }))
            }
            let data = [...state.comboSubAltResponse];
            const header = data[1];
            const result = []
            for (let i = 2; i < data.length; i++) {
                const item = {}
                const productIDs = []

                for (let index = 0; index < data[i].length; index++) {
                    const value = data[i][index]
                    const key = comboSubAltKeyMap[header[index]]

                    if (index >= 3) {
                        productIDs.push(value)
                    } else {
                        if (key === 'enable') item[key] = value ? getters.toBoolean(value) : false
                        else {
                            item[key] = value ? value : null
                        }
                    }
                }
                item["productIDs"] = parseIdList(productIDs)
                result.push(item)
            }
            console.log('替換子項', result)
            return result
        },
        spreadSheetTitles: (state) => {
            return state.spreadSheetData.sheetTitles
        }
    },
    mutations: {
        setDineInTables(state, payload) {
            state.dineInTablesResponse = payload;
            state.dineInTablesLoaded = true;
        },
        setCategories(state, payload) {
            state.categoriesResponse = payload;
        },
        setProducts(state, payload) {
            state.productsResponse = payload;
        },
        setCombs(state, payload) {
            state.combosResponse = payload;
        },
        setSpecifications(state, payload) {
            state.specificationResponse = payload;
        },
        setComboSubItems(state, payload) {
            state.comboSubItemsResponse = payload;
        },
        setComboSubAny(state, payload) {
            state.comboSubAnyResponse = payload;
        },
        setComboSubAlt(state, payload) {
            state.comboSubAltResponse = payload;
        },
        setSpreadSheetData(state, payload) {
            state.spreadSheetData = payload
        },
        setProductComboSpec(state, payload) {
            state.productComboSpecResponse = payload;
        },
        setDineInTablesLoaded(state, payload) {
            state.dineInTablesLoaded = payload
        },
        setDbMenuData(state, payload) {
            state.dbMenuData = payload
        },
        setUseDbData(state, value) {
            state.useDbData = value
        },
        setDbServeAreas(state, payload) {
            state.dbServeAreas = payload
        },
        setDbServeTables(state, payload) {
            state.dbServeTables = payload
        },
        setDbStockItems(state, payload) {
            state.dbStockItems = payload || []
        },
        // 第二層：全單規格背景載入完成後就地補進 dbMenuData
        setSheetSpecArrangements(state, payload) {
            if (!state.dbMenuData) return
            state.dbMenuData = {
                ...state.dbMenuData,
                sheetSpecArrangements: payload || []
            }
        },
        setSecondaryLoadPromise(state, payload) {
            state.secondaryLoadPromise = payload
        },
        setRetailGroups(state, payload) {
            state.retailGroupsResponse = Array.isArray(payload) ? payload : []
        }
    },
    actions: {
        // 清空資料
        async clearData({ commit }) {
            commit('setDineInTables', []);
            commit('setCategories', []);
            commit('setProducts', []);
            commit('setCombs', []);
            commit('setSpecifications', []);
            commit('setComboSubItems', []);
            commit('setComboSubAny', []);
            commit('setComboSubAlt', []);
            commit('setProductComboSpec', []);
            commit('setSpreadSheetData', {});
            commit('setDineInTablesLoaded', false);
            commit('setDbMenuData', null);
            commit('setUseDbData', false);
            commit('setDbServeAreas', null);
            commit('setDbServeTables', null);
            commit('setDbStockItems', []);
            commit('setRetailGroups', []);
            commit('setSecondaryLoadPromise', null);
        },
        // 分類
        async getCategories({ commit, getters }) {
            const response = await spreadsheetApi.getSpreadSheetDataBySheetName(getters.spreadSheetTitles[0]);
            commit('setCategories', response.data.values);
        },
        // 套餐
        async getCombos({ commit, getters, dispatch }) {
            const response = await spreadsheetApi.getSpreadSheetDataBySheetName(getters.spreadSheetTitles[1]);
            commit('setCombs', response.data.values);

            // 套餐資料更新時重置商品描述載入狀態，確保描述資料與套餐資料同步
            dispatch('product/onProductDataUpdated', null, { root: true })
        },
        // 餐點
        async getProducts({ commit, getters, dispatch }) {
            const response = await spreadsheetApi.getSpreadSheetDataBySheetName(getters.spreadSheetTitles[2]);
            commit('setProducts', response.data.values);

            // 商品資料更新時重置商品描述載入狀態，確保描述資料與商品資料同步
            dispatch('product/onProductDataUpdated', null, { root: true })
        },
        // 規格
        async getSpecifications({ commit, getters }) {
            const response = await spreadsheetApi.getSpreadSheetDataBySheetName(getters.spreadSheetTitles[3]);
            commit('setSpecifications', response.data.values);
        },
        // 套餐子項
        async getComboSubItems({ commit, getters }) {
            const response = await spreadsheetApi.getSpreadSheetDataBySheetName(getters.spreadSheetTitles[4]);
            commit('setComboSubItems', response.data.values);
        },
        // 替換子項
        async getComboSubAlt({ commit, getters }) {
            const response = await spreadsheetApi.getSpreadSheetDataBySheetName(getters.spreadSheetTitles[5]);
            commit('setComboSubAlt', response.data.values);
        },
        // 任選子項
        async getComboSubAny({ commit, getters }) {
            const response = await spreadsheetApi.getSpreadSheetDataBySheetName(getters.spreadSheetTitles[6]);
            commit('setComboSubAny', response.data.values);
        },
        // 全單規格
        async getProductComboSpec({ commit, getters }) {
            const response = await spreadsheetApi.getSpreadSheetDataBySheetName(getters.spreadSheetTitles[7]);
            commit('setProductComboSpec', response.data.values);
        },
        // 用餐區域桌號
        async getDineInTables({ commit, getters }) {
            const response = await spreadsheetApi.getSpreadSheetDataBySheetName(getters.spreadSheetTitles[8]);
            commit('setDineInTables', response.data.values);
        },
        // 零售組合
        async getRetailGroups({ commit, getters }) {
            const sheetTitle = getters.spreadSheetTitles?.[9]
            if (!sheetTitle) {
                commit('setRetailGroups', [])
                return
            }

            const response = await spreadsheetApi.getSpreadSheetDataBySheetName(sheetTitle)
            let data = response.data.values || []
            const headerIndex = data.findIndex(row => row.includes('零售組合 ID'))
            const headers = data[headerIndex]
            if (headerIndex === -1 || !headers) {
                commit('setRetailGroups', [])
                return
            }

            data = data.map(row => {
                const diff = headers.length - row.length
                if (diff > 0) {
                    for (let i = 0; i < diff; i++) {
                        row.push(null)
                    }
                }
                return row
            })

            const result = []
            for (let i = headerIndex + 1; i < data.length; i++) {
                const item = {}
                const productIds = []

                for (let index = 0; index < data[i].length; index++) {
                    const value = data[i][index]
                    const key = retailGroupKeyMap[headers[index]]

                    if (index >= 8) {
                        if (value) productIds.push(value)
                    } else if (key === 'availableInStore') {
                        item[key] = value ? getters.toBoolean(String(value).toLowerCase()) || value === '1' : false
                    } else if (key) {
                        item[key] = value || ''
                    }
                }

                item.productIds = productIds
                if (item.id) result.push(item)
            }

            commit('setRetailGroups', result)
        },
        // DB 優先載入桌號；DB 菜單存在但未設定桌號時，不 fallback 至 Google Sheets
        async loadDineInTables({ commit, dispatch, getters, rootGetters }) {
            try {
                const res = await withRetry(() => menuHiveApi.getMenuHiveServeAreas())
                const { total_count, ServeArea_list } = res.data.data
                if (total_count > 0) {
                    const tablesRes = await withRetry(() => menuHiveApi.getMenuHiveServeTables())
                    const serveTablesData = tablesRes.data.data
                    commit('setDbServeAreas', ServeArea_list)
                    commit('setDbServeTables', serveTablesData.ServeTable_list || [])
                    commit('setDineInTablesLoaded', true)
                    console.log('[桌號] 使用 DB 資料')
                    return
                }

                const categoriesRes = await withRetry(() => menuHiveApi.getMenuHiveCategories())
                const categoriesData = categoriesRes.data.data
                if (categoriesData.total_count > 0) {
                    commit('setDbServeAreas', [])
                    commit('setDbServeTables', [])
                    commit('setDineInTablesLoaded', true)
                    console.log('[桌號] DB 菜單無桌號資料')
                    return
                }
            } catch (e) {
                console.warn('[桌號] DB API 失敗，fallback 至 Google Sheets', e)
            }
            // Fallback: GS（保持原有條件，沒有 googleSheetId 就不載入）
            const googleSheetId = rootGetters['basic/googleSheetId']
            if (!googleSheetId) {
                commit('setDineInTablesLoaded', true)
                return
            }
            if (!Array.isArray(getters.spreadSheetTitles) || getters.spreadSheetTitles.length <= 8) {
                await dispatch('getSpreadSheetData')
            }
            await dispatch('getDineInTables')
            commit('setDineInTablesLoaded', true)
            console.log('[桌號] 使用 Google Sheets 資料')
        },
        // 取得菜單頁籤資料
        async getSpreadSheetData({ commit, rootGetters }) {
            // 未設定 sheet 網址時 sheet id 為空字串，送出去會變成 spreadsheets/?key=
            // 這種無效網址，換來 404 與 CORS 錯誤，因此直接略過
            if (!rootGetters['basic/googleSheetId']) {
                console.log('[菜單] 未設定 Google Sheet 網址，略過頁籤資料載入')
                return
            }
            const spreadSheetMetaData = (await spreadsheetApi.getSpreadSheetMetaData()).data
            const spreadSheetData = new spreadSheetModel(spreadSheetMetaData)
            console.log('菜單頁籤資料', spreadSheetData)
            commit('setSpreadSheetData', spreadSheetData)
        },
        // DB 優先載入菜單。
        // 僅在「DB 確實沒有菜單」（categories.total_count === 0）時 fallback 至 Google Sheets。
        // 確定走 DB 之後，任何 API 失敗都經 withRetry 重試，仍失敗則往上拋錯，絕不靜默降級。
        async loadMenuData({ commit, dispatch }) {
            // ── 探測階段：只用 categories 一支決定資料來源 ──
            let categoriesRes
            try {
                categoriesRes = await withRetry(() => menuHiveApi.getMenuHiveCategories())
            } catch (e) {
                console.warn('[菜單] categories API 失敗，fallback 至 Google Sheets', e)
                return dispatch('loadSheetMenuData')
            }

            const categoriesData = categoriesRes.data.data
            if (!categoriesData || categoriesData.total_count === 0) {
                console.log('[菜單] DB 無菜單資料，使用 Google Sheets')
                return dispatch('loadSheetMenuData')
            }

            // ── 第一層：畫面渲染或訂單正確性必需，全成功才算成功；失敗直接往上拋 ──
            const [
                productsRes, specsRes, combosRes,
                comboSubsRes, comboSubAltRes, comboSubAnyRes,
                stockItemsRes, retailGroupsRes
            ] = await Promise.all([
                withRetry(() => menuHiveApi.getMenuHiveProducts()),
                withRetry(() => menuHiveApi.getMenuHiveProductSpecs()),
                withRetry(() => menuHiveApi.getMenuHiveCombos()),
                withRetry(() => menuHiveApi.getMenuHiveComboSubs()),
                withRetry(() => menuHiveApi.getMenuHiveComboSubAlt()),
                withRetry(() => menuHiveApi.getMenuHiveComboSubAny()),
                // 庫存留第一層：送單前無二次驗證，isProductSoldOut() 是唯一售完防線
                withRetry(() => menuHiveApi.getMenuHiveStockItems()),
                // 零售組合留第一層（影響分類版面），但允許降級為空陣列。
                // retries: 0 = 單次嘗試（8 秒 timeout 到即放棄）——失敗可降級的資料
                // 不值得讓使用者陪等重試；正式站確認穩定後可調回 retries: 1
                withRetry(() => menuHiveApi.getMenuHiveRetailGroups(), { retries: 0 }).catch(() => null)
            ])

            commit('setDbMenuData', {
                categories: categoriesData.GoodsCategories_list,
                products: productsRes.data.data.Product_list,
                productSpecs: specsRes.data.data.ProductSpecs_list,
                combos: combosRes.data.data.Combo_list,
                comboSubs: comboSubsRes.data.data.ComboSub_list,
                comboSubAlts: comboSubAltRes.data.data.ComboSubAlt_list,
                comboSubAnyList: comboSubAnyRes.data.data.ComboSubAny_list,
                // 第二層背景補上，先給空陣列避免 getter 取值出錯
                sheetSpecArrangements: []
            })
            commit('setDbStockItems', stockItemsRes.data.data.StockItem_list || [])
            commit('setRetailGroups', retailGroupsRes?.data?.data?.RetailGroups_list || [])
            commit('setUseDbData', true)
            console.log('[菜單] 使用 DB 資料')

            // ── 第二層：不 await，promise 存入 store 供 OrderConfirm 使用 ──
            dispatch('loadSecondaryMenuData')
        },

        // 第二層：Ordering 頁不需要，但 OrderConfirm 的 initializeOrderSpecs() 依賴它。
        // 不可純 fire-and-forget，否則極速結帳會讀到空陣列，靜默套用錯誤的規格預設值。
        loadSecondaryMenuData({ commit }) {
            const promise = withRetry(() => menuHiveApi.getMenuHiveSheetSpecArrangements())
                .then(res => {
                    commit('setSheetSpecArrangements', res.data.data.SheetSpecArrangements_list || [])
                })
                .catch(e => {
                    console.warn('[菜單] 全單規格載入失敗', e)
                    // 失敗時清空 promise，讓 OrderConfirm 進入時 ensureSecondaryMenuData 再補打一次；
                    // 不清空的話，失敗的 promise 會永遠留在 store，之後不再有任何重試機會。
                    commit('setSecondaryLoadPromise', null)
                })
            commit('setSecondaryLoadPromise', promise)
            return promise
        },

        // 供 OrderConfirm 呼叫：已完成則立即 resolve；
        // 從未載入（直接跳網址進入）或上次載入失敗（promise 已清空）則補打一次。
        // 補打仍失敗也 resolve——全單規格套預設值照常結帳，不阻擋流程（見決策四）。
        async ensureSecondaryMenuData({ state, dispatch }) {
            if (!state.useDbData) return
            if (!state.secondaryLoadPromise) return dispatch('loadSecondaryMenuData')
            return state.secondaryLoadPromise
        },

        // 第三層：純顯示資料，失敗只是少圖 / 少簡介，菜單與訂單皆正確。
        // 必須在第一層完成後才呼叫——兩支都依賴 spreadsheet/products、combos、retailGroups getter
        // 來計算 page_size / product_id（見 product.module.js:157-161、:183-186）。
        // 兩支彼此可並行。
        loadTertiaryMenuData({ dispatch }) {
            dispatch('product/getProductImages', undefined, { root: true })
                .catch(e => console.warn('[菜單] 商品圖片載入失敗', e))
            dispatch('product/fetchProductDescriptions', null, { root: true })
                .catch(e => console.warn('[菜單] 商品簡介載入失敗', e))
        },

        // Google Sheets fallback（由原 loadMenuData 尾段抽出，行為不變）
        async loadSheetMenuData({ commit, dispatch, getters, rootGetters }) {
            commit('setUseDbData', false)
            commit('setDbStockItems', [])
            commit('setRetailGroups', [])
            // DB 無菜單又沒設定 sheet 網址時無資料可載，直接收尾避免打出無效請求
            if (!rootGetters['basic/googleSheetId']) {
                console.log('[菜單] 未設定 Google Sheet 網址，無菜單資料可載入')
                return
            }
            if (!Array.isArray(getters.spreadSheetTitles) || getters.spreadSheetTitles.length <= 7) {
                await dispatch('getSpreadSheetData')
            }
            await Promise.all([
                dispatch('getProducts'),
                dispatch('getCombos'),
                dispatch('getCategories'),
                dispatch('getSpecifications'),
                dispatch('getComboSubItems'),
                dispatch('getComboSubAny'),
                dispatch('getComboSubAlt'),
                dispatch('getProductComboSpec'),
                dispatch('getRetailGroups')
            ])
        }
    }
}
